import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getApplicationsList } from "@/services/applications";
import { createAdditionalLoan } from "@/services/loanservices";
import { getBusinessDetails } from "@/services/business";

import {
  ADDITIONAL_LOAN_MODAL_ACTION,
  resolveBannerCtaIntent,
} from "./additional_loan_overview_banner.config";

/**
 * Fetches application + business context, derives eligibility, and runs additional-loan navigation.
 * Keeps UI components thin so new modal rules or API steps mostly touch this hook or the config file.
 */
export function useAdditionalLoanOverviewBanner(loanUUID, loanSummary) {
  const navigate = useNavigate();
  const [modalVariant, setModalVariant] = useState(null);
  const [creatingLoan, setCreatingLoan] = useState(false);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [currentLoanMeta, setCurrentLoanMeta] = useState(null);

  const { eligible_for_additional_rbf_loan } = loanSummary || {};
  const { eligible_for_additional_financing } = businessDetails || {};
  const isEligible = Boolean(eligible_for_additional_rbf_loan || eligible_for_additional_financing);
  const childLoan = currentLoanMeta?.child_loan;
  const hasChildLoan = Boolean(childLoan);
  const isOverdue = (loanSummary?.currentDpd ?? 0) > 0;

  const fetchApplicationStatus = useCallback(async () => {
    try {
      const response = await getApplicationsList();
      const results = response?.results || [];
      const currentLoan = results.find((loan) => loan.uuid === loanUUID);
      setCurrentLoanMeta(currentLoan || null);
    } catch (err) {
      console.warn("Failed to fetch application status", err);
    }
  }, [loanUUID]);

  const fetchBusinessDetails = useCallback(async () => {
    try {
      const res = await getBusinessDetails();
      setBusinessDetails(res?.results || null);
    } catch (err) {
      console.warn("Failed to fetch business details", err);
    }
  }, []);

  useEffect(() => {
    fetchApplicationStatus();
    fetchBusinessDetails();
  }, [fetchApplicationStatus, fetchBusinessDetails]);

  const closeModal = useCallback(() => setModalVariant(null), []);

  const handleLoanAction = useCallback(async () => {
    try {
      const child = currentLoanMeta?.child_loan;
      if (!child) {
        setCreatingLoan(true);

        const payload = {
          loan_uuid: loanUUID,
          type_of_financing: "revenue",
        };

        const res = await createAdditionalLoan(payload);

        const newLoan = res?.results?.loan;
        const newUUID = newLoan?.uuid;

        setCurrentLoanMeta((prev) => ({
          ...(prev || {}),
          next_loan: newLoan,
        }));

        navigate(`/los/${newUUID}/apply`);
        return;
      }
      if (["active", "closed"].includes(child.stage)) {
        navigate(`/lms/${child.uuid}/dashboard`);
      } else {
        navigate(`/los/${child.uuid}/apply`);
      }
    } catch (err) {
      console.error("Failed to create new loan", err);
    } finally {
      setCreatingLoan(false);
      closeModal();
    }
  }, [closeModal, currentLoanMeta, loanUUID, navigate]);

  const onModalAction = useCallback(
    (actionId) => {
      switch (actionId) {
        case ADDITIONAL_LOAN_MODAL_ACTION.DISMISS:
          closeModal();
          break;
        case ADDITIONAL_LOAN_MODAL_ACTION.PAY_OVERDUE:
          closeModal();
          navigate(`/lms/${loanUUID}/loan-information`);
          break;
        case ADDITIONAL_LOAN_MODAL_ACTION.PROCEED:
          void handleLoanAction();
          break;
        default:
          break;
      }
    },
    [closeModal, handleLoanAction, loanUUID, navigate]
  );

  const handleBannerCta = useCallback(async () => {
    const intent = resolveBannerCtaIntent({ hasChildLoan, isOverdue, isEligible });
    if (intent.kind === "loan_action") {
      await handleLoanAction();
      return;
    }
    setModalVariant(intent.variant);
  }, [handleLoanAction, hasChildLoan, isEligible, isOverdue]);

  return {
    modalVariant,
    closeModal,
    onModalAction,
    handleBannerCta,
    creatingLoan,
    hasChildLoan,
  };
}
